import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Instagram, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { reservationSchema } from "@/lib/validation";

const ContattiSection = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefono: "",
    data: "",
    ora: "20:00", // Default time
    persone: "",
    note: ""
  });



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate with zod schema
      const validatedData = reservationSchema.parse({
        nome: formData.nome,
        email: formData.email,
        telefono: formData.telefono,
        data: formData.data,
        ora: formData.ora,
        persone: parseInt(formData.persone),
        note: formData.note
      });

      // Validate date (must be in the future)
      const selectedDate = new Date(validatedData.data);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        throw new Error("La data deve essere nel futuro");
      }

      // Check if date is in blocked period (8-11 December 2025)
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth(); // 0-indexed, so 11 = December
      const day = selectedDate.getDate();

      if (year === 2025 && month === 11 && day >= 8 && day <= 11) {
        throw new Error("Il ristorante è chiuso fino all'11 dicembre. Riapriamo il 12 dicembre!");
      }

      // Call edge function to create reservation with rate limiting
      const { data, error } = await supabase.functions.invoke('create-reservation', {
        body: {
          nome: validatedData.nome,
          email: validatedData.email,
          telefono: validatedData.telefono,
          data: validatedData.data,
          ora: validatedData.ora,
          persone: validatedData.persone,
          note: validatedData.note
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Errore durante la creazione della prenotazione');
      }

      // Success message
      toast({
        title: "Prenotazione inviata con successo!",
        description: "La contatteremo presto per confermare la sua prenotazione.",
      });

      // Reset form
      setPrivacyAccepted(false);
      setFormData({
        nome: "",
        email: "",
        telefono: "",
        data: "",
        ora: "20:00",
        persone: "",
        note: ""
      });

    } catch (error) {
      let errorMessage = "Errore nell'invio della prenotazione. Riprova più tardi.";
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Errore",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'persone' ? parseInt(value) || 2 : value
    });
  };

  return (
    <section id="contatti" className="section-padding bg-gradient-subtle">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-section-title text-primary mb-4">
            Prenota il Tuo <span className="text-gold">Tavolo</span>
          </h2>
          <p className="text-body-large text-muted-foreground max-w-2xl mx-auto font-serif">
            Prenota il tuo tavolo al <strong>ristorante Da Armando a Passignano sul Trasimeno</strong>. Compila il modulo sottostante per richiedere una prenotazione: ti contatteremo entro breve per confermare la disponibilità e accoglierti sulla nostra terrazza con vista lago.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <article className="space-y-8">
            <Card className="card-elegant p-6">
              <h3 className="text-2xl font-bold text-primary mb-4">Contatti Ristorante</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Phone className="text-gold flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-primary">Telefono Ristorante</h4>
                    <p className="text-muted-foreground">
                      <a href="tel:+390753880397">+39 075 388 0397</a>
                    </p>
                    <p className="text-sm text-muted-foreground">Prenotazioni ristorante e informazioni</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="text-gold flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-primary">Email</h4>
                    <p className="text-muted-foreground">
                      <a href="mailto:info@ristorantearmando.it">info@ristorantearmando.it</a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <MapPin className="text-gold flex-shrink-0 mt-1" size={20} />
                  <address className="not-italic">
                    <h4 className="font-semibold text-primary">Indirizzo Ristorante</h4>
                    <p className="text-muted-foreground">Via Aganoor Pompili, 25</p>
                    <p className="text-muted-foreground">06065 Passignano sul Trasimeno (PG)</p>
                    <p className="text-muted-foreground">Umbria, Italia</p>
                  </address>
                </div>


              </div>
            </Card>

            {/* Opening Hours Card */}
            <Card className="card-elegant p-6 bg-gradient-subtle">
              <h4 className="font-semibold text-primary mb-4">Orari Apertura Ristorante</h4>
              
              {/* Estate */}
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gold mb-2">🌞 ESTATE (7/7 + Giovedì pomeriggio)</h5>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Lunedì - Domenica</span>
                    <time className="text-foreground font-medium">12:00-15:00 | 19:00-23:00</time>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Giovedì</span>
                    <time className="text-foreground font-medium">Solo sera: 19:00-23:00</time>
                  </div>
                </div>
              </div>

              {/* Inverno */}
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gold mb-2">❄️ INVERNO (6/7 - Giovedì chiuso)</h5>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Lunedì - Mercoledì</span>
                    <time className="text-foreground font-medium">12:00-15:00 | 19:00-23:00</time>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Venerdì - Domenica</span>
                    <time className="text-foreground font-medium">12:00-15:00 | 19:00-23:00</time>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Giovedì</span>
                    <span className="text-red-500 font-medium">Chiuso</span>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-4 italic">
                * Orari soggetti a variazioni durante le festività
              </p>
            </Card>
          </article>

          {/* Reservation Form */}
          <article>
            <Card className="card-elegant p-6">
              <h3 className="text-2xl font-bold text-primary mb-4">Modulo Prenotazione Online</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Nome Completo *
                    </label>
                    <Input 
                      name="nome" 
                      value={formData.nome} 
                      onChange={handleChange} 
                      placeholder="Il suo nome" 
                      required 
                      className="w-full" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Email *
                    </label>
                    <Input 
                      name="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      placeholder="email@esempio.it" 
                      required 
                      className="w-full" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Telefono *
                    </label>
                    <Input 
                      name="telefono" 
                      type="tel" 
                      value={formData.telefono} 
                      onChange={handleChange} 
                      placeholder="+39 123 456 7890" 
                      required
                      className="w-full" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Numero Persone *
                    </label>
                    <Input 
                      name="persone" 
                      type="number" 
                      min="1" 
                      max="20" 
                      value={formData.persone} 
                      onChange={handleChange} 
                      placeholder="2" 
                      required 
                      className="w-full" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Data Preferita *
                    </label>
                    <Input 
                      name="data" 
                      type="date" 
                      value={formData.data} 
                      onChange={handleChange} 
                      min={new Date().toISOString().split('T')[0]}
                      required 
                      className="w-full" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-2">
                      Ora Preferita
                    </label>
                    <select 
                      name="ora" 
                      value={formData.ora} 
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                    >
                      <optgroup label="Pranzo (12:00-15:00)">
                        <option value="12:00">12:00</option>
                        <option value="12:30">12:30</option>
                        <option value="13:00">13:00</option>
                        <option value="13:30">13:30</option>
                        <option value="14:00">14:00</option>
                        <option value="14:30">14:30</option>
                      </optgroup>
                      <optgroup label="Cena (18:00-23:00)">
                        <option value="18:00">18:00</option>
                        <option value="18:30">18:30</option>
                        <option value="19:00">19:00</option>
                        <option value="19:30">19:30</option>
                        <option value="20:00">20:00</option>
                        <option value="20:30">20:30</option>
                        <option value="21:00">21:00</option>
                        <option value="21:30">21:30</option>
                        <option value="22:00">22:00</option>
                        <option value="22:30">22:30</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-2">
                    Note Aggiuntive
                  </label>
                  <Textarea 
                    name="note" 
                    value={formData.note} 
                    onChange={handleChange} 
                    placeholder="Allergie, intolleranze, occasioni speciali..." 
                    rows={4} 
                    className="w-full" 
                  />
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="privacy-consent"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-gold focus:ring-gold"
                    required
                  />
                  <label htmlFor="privacy-consent" className="text-sm text-muted-foreground">
                    Ho letto e accetto l'<a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-gold underline hover:text-primary">Informativa sulla Privacy</a> e acconsento al trattamento dei miei dati personali per la gestione della prenotazione. *
                  </label>
                </div>

                <Button
                  type="submit"
                  className="btn-hero w-full"
                  disabled={loading || !privacyAccepted}
                >
                  {loading ? "Invio in corso..." : "Invia Prenotazione"}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  * Campi obbligatori. Riceverai conferma della prenotazione entro 24 ore.
                </p>
              </form>
            </Card>
          </article>
        </div>
      </div>
    </section>
  );
};

export default ContattiSection;